import { FileUploadFile } from 'primereact/fileupload';
import { z } from 'zod';

import { MAX_IMAGE_FILE_SIZE, MAX_VIDEO_FILE_SIZE } from '@features/campaigns/builder/consts/common.ts';
import { CREATIVE_CATEGORY, CreativeCategory } from '@features/campaigns/builder/types/creativeType.ts';
import {
  areCreativeDimensionsValid,
  CreativeImageDimensions,
  DEFAULT_CREATIVE_IMAGE_DIMENSIONS,
  ExtendedFile,
} from '@features/campaigns/builder/utils/areCreativeDimensionsValid.ts';
import {
  encodeCreativeValidationErrorMessage,
  CreativeValidationErrorMessageParams,
} from '@features/campaigns/builder/utils/creativeValidationErrorMessage.ts';
import { isCreativeFormatValid } from '@features/campaigns/builder/utils/isCreativeFormatValid.ts';
import { isCreativeSizeValid } from '@features/campaigns/builder/utils/isCreativeSizeValid.ts';

export interface CreativeImageValidationConstraints {
  allowedFormats: string[];
  maxFileSize?: number;
  resolveMaxFileSize?: (file: File) => number;
  dimensions?: CreativeImageDimensions;
}

function resolveImageMaxFileSize(file: File, constraints: CreativeImageValidationConstraints): number {
  if (constraints.resolveMaxFileSize) {
    return constraints.resolveMaxFileSize(file);
  }

  return constraints.maxFileSize ?? MAX_IMAGE_FILE_SIZE;
}

export function checkCreativeFileType(files: FileUploadFile[]): CreativeCategory | undefined {
  const file = files[0];

  const imageType = file.type.match(/^image\//);
  const videoType = file.type.match(/^video\//);

  if (imageType) {
    return CREATIVE_CATEGORY.IMAGE;
  }

  if (videoType) {
    return CREATIVE_CATEGORY.VIDEO;
  }

  return;
}

export async function validateCreativeImageFile(file: ExtendedFile[], constraints: CreativeImageValidationConstraints) {
  const maxFileSize = resolveImageMaxFileSize(file[0], constraints);
  const isSizeValid = isCreativeSizeValid(file, maxFileSize);
  const isFileFormatValid = isCreativeFormatValid(file, constraints.allowedFormats);
  const areDimensionsValid = await areCreativeDimensionsValid(file, constraints.dimensions);
  const isFileValid = isSizeValid && isFileFormatValid && areDimensionsValid;

  return {
    isSizeValid,
    isFileFormatValid,
    areDimensionsValid,
    isFileValid,
    maxFileSize,
  };
}

export function validateCreativeVideoFile(file: ExtendedFile[], allowedFormats: string[]) {
  const isSizeValid = isCreativeSizeValid(file, MAX_VIDEO_FILE_SIZE);
  const isFileFormatValid = isCreativeFormatValid(file, allowedFormats);

  const isFileValid = isFileFormatValid && isSizeValid;

  return {
    isFileFormatValid,
    isSizeValid,
    isFileValid,
  };
}

function addCreativeImageValidationIssues(
  ctx: z.RefinementCtx,
  validationResult: {
    isFileFormatValid: boolean;
    isSizeValid: boolean;
    areDimensionsValid: boolean;
  },
  messageParams: CreativeValidationErrorMessageParams,
) {
  if (!validationResult.isFileFormatValid) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'errorMessages.creativeFileWrongFormat',
    });
  }

  if (!validationResult.isSizeValid && !validationResult.areDimensionsValid) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: encodeCreativeValidationErrorMessage(
        'errorMessages.comboCreativeFileWrongDimensionsWrongSize',
        messageParams,
      ),
    });
    return;
  }

  if (!validationResult.isSizeValid) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: encodeCreativeValidationErrorMessage('errorMessages.creativeImageFileWrongSize', {
        maxSizeKb: messageParams.maxSizeKb,
      }),
    });
  }

  if (!validationResult.areDimensionsValid) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: encodeCreativeValidationErrorMessage('errorMessages.creativeFileWrongDimensions', {
        width: messageParams.width,
        height: messageParams.height,
      }),
    });
  }
}

export async function creativeImageFileValidationCheck(
  file: ExtendedFile[] | string | null | undefined,
  ctx: z.RefinementCtx,
  constraints: CreativeImageValidationConstraints,
) {
  if (file === null || file === undefined || typeof file === 'string') {
    return true;
  }

  const { isFileFormatValid, isSizeValid, areDimensionsValid, maxFileSize } = await validateCreativeImageFile(
    file,
    constraints,
  );
  const dimensions = constraints.dimensions ?? DEFAULT_CREATIVE_IMAGE_DIMENSIONS;

  addCreativeImageValidationIssues(
    ctx,
    { isFileFormatValid, isSizeValid, areDimensionsValid },
    {
      width: dimensions.width,
      height: dimensions.height,
      maxSizeKb: Math.round(maxFileSize / 1024),
    },
  );
}

export function creativeVideoFileValidationCheck(
  file: ExtendedFile[] | string | null,
  ctx: z.RefinementCtx,
  allowedFormats: string[],
) {
  if (file === null || typeof file === 'string') {
    return true;
  }

  const { isSizeValid, isFileFormatValid } = validateCreativeVideoFile(file, allowedFormats);

  if (!isFileFormatValid) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'errorMessages.creativeFileWrongFormat',
    });
  }

  if (!isSizeValid) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'errorMessages.creativeVideoFileWrongSize',
    });
  }
}
